/**
 * ServicesContext - React context for service registry
 * Provides services to all components in the application
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { serviceRegistry } from '../../services/core/ServiceRegistry';
import { useAuth } from '../auth/CognitoAuthContext';

const ServicesContext = createContext(null);

export const ServicesProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [services, setServices] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;

    const initializeServices = async () => {
      try {
        setLoading(true);
        setError(null);

        // Wait for service registry to initialize
        await serviceRegistry.initialize();

        // Get all services
        const allServices = await serviceRegistry.getAllServices();

        if (mounted) {
          setServices(allServices);
          setLoading(false);
        }

      } catch (error) {
        console.error('Failed to initialize services:', error);
        if (mounted) {
          setError(error.message);
          setLoading(false);
        }
      }
    };

    initializeServices();

    // Cleanup
    return () => {
      mounted = false;
    };
  }, [currentUser]);

  // Update auth provider when user changes
  useEffect(() => {
    if (currentUser && services) {
      // Auth provider is already handled by the registry
      // This effect is here for any future auth-related service updates
    }
  }, [currentUser, services]);

  const contextValue = {
    // Individual services
    influencerService: services?.influencer,
    imageService: services?.images,
    processingService: services?.processing,
    // trainingService: Use static TrainingService from '../services/api/trainingService' instead

    // Service registry for advanced usage
    registry: serviceRegistry,

    // State
    loading,
    error,
    ready: !loading && !error && services !== null,

    // Actions
    reload: async () => {
      setLoading(true);
      try {
        await serviceRegistry.reload();
        const allServices = await serviceRegistry.getAllServices();
        setServices(allServices);
        setError(null);
      } catch (error) {
        console.error('Failed to reload services:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    },

    healthCheck: async () => {
      if (!services) return [];
      return serviceRegistry.healthCheck();
    }
  };

  return (
    <ServicesContext.Provider value={contextValue}>
      {children}
    </ServicesContext.Provider>
  );
};

/**
 * Hook to use services in components
 */
export const useServices = () => {
  const context = useContext(ServicesContext);

  if (!context) {
    throw new Error('useServices must be used within a ServicesProvider');
  }

  return context;
};

/**
 * Hook to use a specific service
 */
export const useService = (serviceName) => {
  const { [serviceName]: service, loading, error } = useServices();

  if (loading) {
    return { service: null, loading: true, error: null };
  }

  if (error) {
    return { service: null, loading: false, error };
  }

  if (!service) {
    return {
      service: null,
      loading: false,
      error: `Service '${serviceName}' not available`
    };
  }

  return { service, loading: false, error: null };
};

/**
 * HOC to inject services into a component
 */
export const withServices = (WrappedComponent) => {
  return (props) => {
    const services = useServices();
    return <WrappedComponent {...props} services={services} />;
  };
};

export default ServicesContext;